import { FC } from "react";
import {
	useController,
	useFormContext,
	useFormState,
	useWatch
} from "react-hook-form";
import { useUpdateEffect } from "react-use";
import { useCreateObservationAnswer } from "../../hooks";
import useUpdateObservationAnswer from "../../hooks/usePutObservationAnswer/usePutObservationAnswer";
import { Trash2, Info } from "lucide-react";
import { Button } from "src/components/ui/button";
import { toast } from "react-toastify";
import { CustomToast } from "src/components/CustomToast";
import { answerExists } from "src/features/observationsAdmin/utils";

interface Props {
	onRemoveAnswerClick: () => void;
	answerIndex: number;
	questionIndex: number;
	isDisabled?: boolean;
	handleAddAnswerOptionClick: () => void;
}

const AnswerRowItem: FC<Props> = ({
	onRemoveAnswerClick,
	questionIndex,
	answerIndex,
	isDisabled
}) => {
	const { mutate: createObservationAnswer, isLoading: isCreateAnswerLoading } =
		useCreateObservationAnswer();

	const { mutate: updateObservationAnswer, isLoading: isUpdateAnswerLoading } =
		useUpdateObservationAnswer();

	const { control, setValue } = useFormContext<AdminObservationForm>();

	const { errors } = useFormState({ control });

	const { field: valueField } = useController({
		control,
		name: `questions.${questionIndex}.answers.${answerIndex}.name`,
		rules: { required: "Please complete required field" }
	});

	const { field: compliantField } = useController({
		control,
		name: `questions.${questionIndex}.answers.${answerIndex}.value`
	});

	const { id: questionId } = useWatch({
		control,
		name: `questions.${questionIndex}`
	});

	const {
		id: answerId,
		value,
		compliant,
		name
	} = useWatch({
		control,
		name: `questions.${questionIndex}.answers.${answerIndex}`
	});

	const answers = useWatch({
		control,
		name: `questions.${questionIndex}.answers`
	});

	const observationType = useWatch({
		control,
		name: "observationType"
	});

	const isCorrectAnswerRequired = useWatch({
		control,
		name: `questions.${questionIndex}.isCorrectAnswerRequired`
	});

	useUpdateEffect(() => {
		if (!answers || isCorrectAnswerRequired) return;

		answers.forEach((answer: AdminObservationAnswer, index: number) => {
			if (!answerId) return;

			setValue(`questions.${questionIndex}.answers.${index}.value`, "");

			const payload = {
				id: answer.id,
				question: questionId,
				name: answer.name,
				order: answer.order,
				compliant: false,
				store_correct: false,
				great_conversation_correct: false
			};

			updateObservationAnswer(payload);
		});
	}, [isCorrectAnswerRequired]);

	useUpdateEffect(() => {
		const compliantValue = value === "yes";

		setValue(
			`questions.${questionIndex}.answers.${answerIndex}.compliant`,
			compliantValue
		);
	}, [value]);

	const onSuccess = ({ id }: AdminObservationAnswer) => {
		setValue(`questions.${questionIndex}.answers.${answerIndex}.id`, id);
	};

	const handleBlur = () => {
		valueField.onBlur();
		if (!questionId || !name) return;

		const existingAnswer = answerExists({
			answers,
			answerGiven: name,
			answerId
		});

		if (existingAnswer) {
			toast.warning(
				<CustomToast
					type="Warning"
					message="Answer with the same name already exists"
				/>,
				{ hideProgressBar: true }
			);

			setValue(`questions.${questionIndex}.answers.${answerIndex}.name`, "");
			return;
		}

		const isCompliant = observationType === "INDIVIDUAL" && compliant;
		const isScoredAssessment = observationType === "STORE_SCORED_ASSESSMENT";
		const isGreatConversation = observationType === "GREAT_CONVERSATION";

		const payload = {
			id: answerId,
			question_id: questionId,
			name,
			order: answerIndex + 1,
			compliant: isCompliant,
			store_correct: isScoredAssessment && compliantField.value === "yes",
			great_conversation_correct:
				isGreatConversation && compliantField.value === "yes"
		};

		if (!answerId) {
			return createObservationAnswer(payload, {
				onSuccess
			});
		}

		updateObservationAnswer(payload);
	};

	const showDeleteButton = answerIndex >= 1;

	const renderRadioGroupFormLabel = () => {
		if (observationType === "INDIVIDUAL") {
			return "Is this answer compliant?";
		}

		if (observationType === "STORE_SCORED_ASSESSMENT") {
			return "Is this answer correct?";
		}

		if (observationType === "GREAT_CONVERSATION") {
			return "Is this answer correct?";
		}

		// Store Self Assessment doesnt have a correct answer radio
	};

	const showCorrectAnswerRadios = () => {
		if (observationType === "STORE_SELF_ASSESSMENT") return false;

		if (observationType === "GREAT_CONVERSATION") {
			// Great Conversation has an option to toggle if the correct answer radio should show
			return isCorrectAnswerRequired;
		}

		return true;
	};

	const isInvalid = Boolean(
		errors.questions?.[questionIndex]?.answers?.[answerIndex]?.name
	);

	const errorMessage =
		errors.questions?.[questionIndex]?.answers?.[answerIndex]?.name?.message;

	return (
		<div className="w-full">
			{/* Labels row */}
			<div className="flex items-end mb-1">
				<div className="w-1/2 shrink-0 flex justify-between pr-3">
					<label className="text-sm font-medium text-zinc-600">
						Answer {answerIndex + 1}
					</label>
					<span className="text-[13px] text-muted-foreground">Required</span>
				</div>
				{showCorrectAnswerRadios() && (
					<div className="ml-auto mr-10">
						<span className="text-[13px] text-muted-foreground">
							{renderRadioGroupFormLabel()}
						</span>
					</div>
				)}
			</div>

			{/* Controls row — input, toggle, bin all h-10 aligned */}
			<div className="flex items-center gap-3">
				<div className={`w-1/2 shrink-0 ${isInvalid ? "has-error" : ""}`}>
					<input
						className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
						placeholder={`Enter answer ${answerIndex + 1}`}
						{...valueField}
						onBlur={handleBlur}
						disabled={
							isDisabled || isCreateAnswerLoading || isUpdateAnswerLoading
						}
					/>
				</div>

				<div className="flex items-center gap-3 ml-auto">
					{showCorrectAnswerRadios() && (
						<div className="flex gap-4 bg-white border border-border rounded-lg px-3 h-10 items-center w-[130px] justify-center shrink-0">
							<label className="flex items-center gap-1.5 cursor-pointer text-sm">
								<input
									type="radio"
									{...compliantField}
									value="yes"
									checked={compliantField.value === "yes"}
									onBlur={handleBlur}
									disabled={
										isDisabled || isCreateAnswerLoading || isUpdateAnswerLoading
									}
								/>
								Yes
							</label>
							<label className="flex items-center gap-1.5 cursor-pointer text-sm">
								<input
									type="radio"
									{...compliantField}
									value="no"
									checked={compliantField.value === "no"}
									onBlur={handleBlur}
									disabled={
										isDisabled || isCreateAnswerLoading || isUpdateAnswerLoading
									}
								/>
								No
							</label>
						</div>
					)}

					<button
						type="button"
						className="shrink-0 w-7 h-7 inline-flex items-center justify-center rounded p-1 text-destructive hover:bg-foreground hover:text-white transition-colors disabled:invisible"
						aria-label="Remove Option"
						onClick={onRemoveAnswerClick}
						disabled={!showDeleteButton}
						tabIndex={showDeleteButton ? 0 : -1}
					>
						<Trash2 size={16} />
					</button>
				</div>
			</div>

			{isInvalid && (
				<div className="flex items-center gap-1 mt-1 w-full text-destructive">
					<Info size={16} />
					<span className="text-xs">{errorMessage}</span>
				</div>
			)}
		</div>
	);
};

export default AnswerRowItem;
